import {
  Flex,
  HStack,
  Spacer,
  useToast,
  Text,
  Icon,
  useColorModeValue,
  AlertDialog,
  AlertDialogBody,
  AlertDialogContent,
  AlertDialogHeader,
  AlertDialogOverlay,
  Box,
  Button,
} from '@chakra-ui/react';
import React, { useState } from 'react';

import {
  date as commonDate,
  finleyAssertUnreachable,
} from '@finley-technologies/common';
import { Util } from '@finley-technologies/finley-types';
import {
  getValleyBankArchiveThresoldDate,
  VALLEY_BANK_ARCHIVE_THRESHOLD_DAYS,
} from '@finley-technologies/valley-bank-utils';

import { useRestoreValleyBankReportingDateMutation } from 'types/dashboard-core-gqlTypes.generated.d';

import {
  DashboardMode,
  DataMetadataContextType,
} from '../hooks/DataMetadataContextType';
import {
  getBgColorForDashboardMode,
  getHeaderElementProps,
  HeaderProps,
} from './utils';
import { ValleyBankPreviewPicker } from './ValleyBankPreviewPicker';
import { colors } from 'packages/common-frontend/colors';
import { ProgressAlert } from 'packages/common-frontend/components/Alert';
import { SimpleDatePicker } from 'packages/common-frontend/components/SimpleDatePicker';
import { HEADER_HEIGHT } from 'packages/header/ui/Header';
import { FiCalendar } from 'react-icons/fi';
import { DownloadMenuButton } from 'valleyBank/domains/page/ui/DownloadMenuButton';

const ConfirmRestoreModal: React.FC<{
  isOpen: boolean;
  onCancel: () => void;
  onSubmit: (confirmedRestoreDate: Util.FullDateStr) => void;
  restoreDate: Util.FullDateStr;
}> = ({ isOpen, onCancel, onSubmit, restoreDate }) => {
  const cancelRef = React.useRef();
  const buttonColorScheme = useColorModeValue('aurora', 'gray');
  return (
    <AlertDialog
      isOpen={isOpen}
      leastDestructiveRef={cancelRef.current}
      onClose={onCancel}
    >
      <AlertDialogOverlay>
        <AlertDialogContent>
          <AlertDialogHeader fontSize="lg" fontWeight="bold">
            Confirm Restore from Archive
          </AlertDialogHeader>

          <AlertDialogBody>
            <>
              <Text
                mb={2}
              >{`Reporting date ${restoreDate} is greater than ${VALLEY_BANK_ARCHIVE_THRESHOLD_DAYS} days old and needs to be restored from the archive.  This will take a few minutes to load. Confirm?`}</Text>
              <Box py="12px" alignSelf="flex-end">
                <HStack spacing="25px">
                  <Spacer />
                  <Button
                    variant="outline"
                    colorScheme={buttonColorScheme}
                    onClick={() => onCancel()}
                    px="20px"
                  >
                    Cancel
                  </Button>
                  <Button
                    variant="solid"
                    colorScheme={buttonColorScheme}
                    onClick={() => {
                      onSubmit(restoreDate);
                    }}
                    px="20px"
                  >
                    Submit
                  </Button>
                </HStack>
              </Box>
            </>
          </AlertDialogBody>
        </AlertDialogContent>
      </AlertDialogOverlay>
    </AlertDialog>
  );
};

const HeaderMessage: React.FC<HeaderProps> = ({
  headerMessage,
  headerIcon,
  headerIconColor,
}) => {
  if (!headerMessage || !headerIcon || !headerIconColor) return null;
  return (
    <HStack color={headerIconColor}>
      <Icon icon={headerIcon} />
      <Text>{headerMessage}</Text>
    </HStack>
  );
};

const AsOfDateDisplay = ({
  asOfDate,
}: {
  asOfDate: Util.FullDateStr | null;
}) => {
  return asOfDate ? (
    <HStack color={colors.black[60]}>
      <Icon icon="clock" />
      <Text>{`Data Sourced: ${commonDate.fullDateStrToHumanDateString(
        asOfDate,
      )}`}</Text>
    </HStack>
  ) : (
    // asOfDate will be lazy loaded on first data fetch
    <ProgressAlert status="info" bgColor="transparent">
      Loading latest data timestamp
    </ProgressAlert>
  );
};

// picks up date for historical mode
const DatePicker: React.FC<{
  maxReportingDate: Util.FullDateStr;
  historicalReportingDate: Util.FullDateStr;
  setHistoricalReportingDate: (date: Util.FullDateStr) => void;
}> = ({
  maxReportingDate,
  historicalReportingDate,
  setHistoricalReportingDate,
}) => {
  const toast = useToast();

  const [isRestoreModalOpen, setIsRestoreModalOpen] = useState<boolean>(false);
  const [restoreDate, setRestoreDate] = useState<Util.FullDateStr | null>(null);

  const maxDate = commonDate.fullDateStrToDate(maxReportingDate);

  const selectedDate = commonDate.fullDateStrToDate(historicalReportingDate);

  const [
    restoreValleyBankReportingDate,
  ] = useRestoreValleyBankReportingDateMutation({
    fetchPolicy: 'network-only',
    onCompleted() {
      toast({
        status: 'success',
        duration: 3000,
        title: 'Initiated restore.',
        position: 'top',
      });
    },
    onError() {
      toast({
        status: 'error',
        position: 'top',
        isClosable: true,
        title:
          'Unable to initiate restore. Please contact Finley for assistance.',
      });
    },
  });

  const setSelectedDate = (date: Date) => {
    const currentSelection = commonDate.dateToFullDateString(date);

    const archiveThresold = getValleyBankArchiveThresoldDate();

    if (currentSelection < archiveThresold) {
      setRestoreDate(currentSelection);
      setIsRestoreModalOpen(true);
    } else {
      setRestoreDate(null);
      setIsRestoreModalOpen(false);
      setHistoricalReportingDate(currentSelection);
    }
  };

  const onSubmit = async (confirmedRestoreDate: Util.FullDateStr) => {
    await restoreValleyBankReportingDate({
      variables: { reportingDate: confirmedRestoreDate },
    });
    setRestoreDate(null);
    setIsRestoreModalOpen(false);
    setHistoricalReportingDate(confirmedRestoreDate);
  };

  const clientTimeZone = Intl.DateTimeFormat().resolvedOptions().timeZone;
  return (
    <Flex>
      {restoreDate && (
        <ConfirmRestoreModal
          isOpen={isRestoreModalOpen}
          onCancel={() => {
            setIsRestoreModalOpen(false);
          }}
          restoreDate={restoreDate}
          onSubmit={onSubmit}
        />
      )}
      <SimpleDatePicker
        width="170px"
        tz={clientTimeZone}
        dateFormat="MMMM dd, yyyy"
        minDate={null} // VB: make db call to find minDate.
        maxDate={maxDate}
        selectedDate={selectedDate}
        showTimeSelect={false}
        setSelectedDate={setSelectedDate}
      />
      <Icon
        as={FiCalendar}
        position="relative"
        right="20px"
        h="inherit"
        pointerEvents="none"
      />
    </Flex>
  );
};
const HeaderMessageForDashboardMode: React.FC<{
  maxReportingDate: Util.FullDateStr;
  currentReportingDate: Util.FullDateStr;
  setReportingDate: (date: Util.FullDateStr) => void;
  dashboardMode: DashboardMode;
  asOfDate: Util.FullDateStr | null;
}> = ({
  dashboardMode,
  asOfDate,
  currentReportingDate,
  maxReportingDate,
  setReportingDate,
}) => {
  const headerElement = getHeaderElementProps(dashboardMode);

  switch (dashboardMode) {
    case 'current':
      return <AsOfDateDisplay asOfDate={asOfDate} />;
    case 'historical':
      return (
        <>
          <DatePicker
            maxReportingDate={maxReportingDate}
            setHistoricalReportingDate={setReportingDate}
            historicalReportingDate={currentReportingDate}
          />
          <AsOfDateDisplay asOfDate={asOfDate} />
        </>
      );

    case 'preview':
      return <HeaderMessage {...headerElement} />;
    default:
      return finleyAssertUnreachable(dashboardMode);
  }
};

export const ValleyBankDataMedataHeader: React.FC<{
  context: DataMetadataContextType;
}> = ({ context }) => {
  const {
    asOfDate,
    dashboardMode,
    isPreview,
    reportingDate,
    setDashboardMode,
    setReportingDate,
    currentReportingDate,
  } = context;

  return (
    <HStack
      w="100%"
      h="84px"
      spacing="24px"
      p="16px 24px"
      position="fixed"
      top={`${HEADER_HEIGHT}px`}
      zIndex="sticky"
      backgroundColor={getBgColorForDashboardMode(dashboardMode)}
      border="1px solid"
      borderColor="gray.200"
    >
      <ValleyBankPreviewPicker
        dashboardMode={dashboardMode}
        setDashboardMode={setDashboardMode}
      />
      <HeaderMessageForDashboardMode
        dashboardMode={dashboardMode}
        asOfDate={asOfDate}
        setReportingDate={setReportingDate}
        currentReportingDate={reportingDate}
        maxReportingDate={commonDate.addDays(currentReportingDate, -1)}
      />
      <Spacer />
      <DownloadMenuButton reportingDate={reportingDate} isPreview={isPreview} />
    </HStack>
  );
};
